import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Chip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';

/**
 * Multi Row Selector Template
 * Based on Figma design: https://www.figma.com/design/Nhga9ODmaPGwSQJjO8lQZi/Calibrate-Mate?node-id=394-10113&m=dev
 * Example: Location Discovery
 * 
 * Design specs from Figma:
 * - Chat bubble with 10px border radius (10px 10px 10px 0px)
 * - Row wrapper with 12px border radius, stroke rgba(192, 197, 205, 0.5)
 * - Selected chips: #E1EFFF background, #0F42BC text
 * - Unselected chips: #FFFFFF background, #465366 text, #EAECF0 border
 * - Sub-headers with 500 font weight, 14px size
 */
const MultiRowSelectorTemplate = ({
  title = "Based on what you're looking for, here are the top locations where similar candidates are most commonly found 🌍\nPick the location(s) you'd like to focus on, or feel free to make the role remote! 🗺️✨",
  sections = [],
  selectedItems = [],
  onConfirmed,
  confirmButtonText = "Confirm Location",
  readOnly = false,
  allowEmptySelection = false,
  showSkipButton = false,
  onSkipDiscovery,
  skipButtonText = "Skip Discovery"
}) => {
  console.log('🎯 MULTIROW TEMPLATE PROPS:', {
    title,
    sections,
    sectionsLength: sections.length,
    firstSectionItems: sections.length > 0 ? sections[0].items : 'no sections',
    selectedItems,
    readOnly
  });
  
  // Get all available items to pre-select by default
  const allAvailableItems = React.useMemo(() => {
    const allItems = [];
    sections.forEach(section => {
      if (section.items) {
        section.items.forEach(item => {
          const itemValue = item.value || item;
          allItems.push(itemValue);
        });
      }
    });
    return allItems;
  }, [sections]);
  
  // Pre-select all items if no specific items are selected AND empty selection is not allowed
  const initialSelectedItems = selectedItems.length > 0 ? selectedItems : (allowEmptySelection ? [] : allAvailableItems);
  const [localSelectedItems, setLocalSelectedItems] = useState(initialSelectedItems);

  // Update local state only when selectedItems prop explicitly changes (not when empty)
  useEffect(() => {
    // Only update if selectedItems prop has actual values - don't reset user selections
    if (selectedItems.length > 0) {
      setLocalSelectedItems(selectedItems);
    }
    // Don't reset to allAvailableItems if user has made selections and then selectedItems becomes empty
  }, [selectedItems]);

  const handleItemToggle = (item) => {
    if (readOnly) return;
    
    setLocalSelectedItems(prev => {
      if (prev.includes(item)) {
        return prev.filter(i => i !== item);
      } else {
        return [...prev, item];
      }
    });
  };

  const handleConfirm = () => {
    if (onConfirmed) {
      onConfirmed(localSelectedItems);
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100%',
        padding: '0px',
        backgroundColor: 'transparent',
        marginLeft: 0, // Align with chat bubble text
      }}
    >
      {/* Chat Bubble Container */}
      <Box
        sx={{
          backgroundColor: '#FFFFFF',
          borderRadius: '10px 10px 10px 0px',
          padding: '0px',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px', // Figma spec
          marginLeft: 0, // Start at same position as AI text
        }}
      >

        {/* Row Wrapper */}
        <Box
          sx={{
            border: '1px solid rgba(192, 197, 205, 0.5)', // Exact Figma color
            borderRadius: '12px',
            padding: '16px 20px',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            alignSelf: 'stretch',
          }}
        >
          {sections.map((section, sectionIndex) => (
            <Box
              key={sectionIndex}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                alignSelf: 'stretch',
              }}
            >
              {/* Chip Row */}
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '4px',
                  alignSelf: 'stretch',
                }}
              >
                {section.items.map((item, itemIndex) => {
                  const itemValue = item.value || item;
                  
                  // ✅ Fix: Ensure itemLabel is always a string
                  let itemLabel;
                  if (typeof item === 'object' && item !== null) {
                    // If item is an object, try to get label, name, or convert to string
                    itemLabel = item.label || item.name || item.value || String(item);
                  } else {
                    // If item is already a string or primitive, use it directly
                    itemLabel = String(item);
                  }
                  
                  // ✅ Fixed: Use consistent selection logic - always use itemValue for consistency
                  const isSelected = localSelectedItems.includes(itemValue);
                  
                  console.log('🔍 MULTIROW SELECTION DEBUG:', {
                    item,
                    itemValue,
                    itemLabel,
                    localSelectedItems,
                    isSelected: localSelectedItems.includes(itemValue)
                  });
                  
                  return (
                    <Chip
                      key={itemIndex}
                      icon={
                        isSelected ? (
                          <CheckCircleIcon sx={{ fontSize: '14px !important' }} />
                        ) : (
                          <RadioButtonUncheckedIcon sx={{ fontSize: '14px !important' }} />
                        )
                      }
                      label={itemLabel}
                      onClick={() => handleItemToggle(itemValue)}
                      sx={{
                        backgroundColor: isSelected ? '#E1EFFF' : '#FFFFFF', // Figma colors
                        color: isSelected ? '#0F42BC' : '#465366', // Figma colors
                        // border: isSelected ? 'none' : '1px solid #EAECF0', // Figma border
                        borderRadius: '16px',
                        padding: '4px 12px 4px 10px',
                        fontFamily: 'Roboto',
                        fontSize: '14px',
                        fontWeight: 400,
                        lineHeight: '1.4285714285714286em',
                        cursor: readOnly ? 'default' : 'pointer',
                        '& .MuiChip-icon': {
                          color: isSelected ? '#0F42BC' : '#465366',
                          fontSize: '14px',
                          fontFamily: 'Material Icons Round',
                        },
                        '&:hover': readOnly ? {} : {
                          backgroundColor: isSelected ? '#D1E7FF' : '#F9FAFB',
                        },
                      }}
                    />
                  );
                })}
              </Box>
            </Box>
          ))}
        </Box>

        {/* Buttons Row - Confirm and Skip side by side */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 2 }}>
          <Button
            variant="contained"
            onClick={readOnly ? undefined : handleConfirm}
            disabled={readOnly || (!allowEmptySelection && localSelectedItems.length === 0)}
            sx={{
              backgroundColor: readOnly ? '#E5E7EB' : '#4599FA', // Gray background for read-only
              color: readOnly ? '#9CA3AF' : '#FFFFFF', // Gray text for read-only
              fontFamily: 'Roboto',
              fontSize: '14px',
              fontWeight: 400,
              textTransform: 'none',
              borderRadius: '8px',
              padding: '8px 12px',
              height: '36px',
              cursor: readOnly ? 'default' : 'pointer',
              '&:hover': readOnly ? {} : {
                backgroundColor: '#3b82d6',
              },
              '&:disabled': {
                backgroundColor: '#E5E7EB',
                color: '#9CA3AF',
              }
            }}
          >
            {readOnly ? 'Confirmed' : (localSelectedItems.length === 0 && allowEmptySelection ? 'Continue' : confirmButtonText)}
          </Button>
          
          {/* Skip Button - only show for editable items */}
          {showSkipButton && !readOnly && (
            <Button
              variant="outlined"
              onClick={onSkipDiscovery}
              sx={{
                color: '#667085',
                borderColor: '#D0D5DD',
                backgroundColor: '#F9FAFB',
                fontWeight: 400,
                fontSize: '14px',
                padding: '8px 16px',
                height: '36px',
                borderRadius: '8px',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#F2F4F7',
                  borderColor: '#B8BCC8',
                }
              }}
            >
              {skipButtonText}
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default MultiRowSelectorTemplate;
